import React from 'react';
import { ProductViewDetails } from '@interfaces/models/internal/analytics-event/product-event';
import useProductShippingQuery from '@hooks/product/use-product-shipping-query';
import useProductQuery from '@hooks/product/use-product-query';
import useHeroPdpStyles from '@hooks/hero-pdp/use-hero-pdp-styles';
import usePdpLayout from '@hooks/product/use-pdp-layout';
import { Product } from '@interfaces/models/product';
import { Shipping } from '@interfaces/models/shipping';
import { useAnalytics } from '@context/analytics.context';
import TagContainer from '@enums/tagContainer';
import Link from '@components/atoms/link/link';
import CTAProduct from '@components/molecules/products/product-details/components/CTA-product/CTA-product';
import { NegotiationProvider } from '@context/negotiation.context';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import styles from './hero-pdp-cta.module.scss';

// VIEW MORE BUTTON COMPONENT
interface ViewMoreButtonProps {
  product: Product;
}

const ViewMoreButton: React.FC<ViewMoreButtonProps> = ({ product }) => {
  const { sendEvent } = useAnalytics();
  const { t } = useTranslation();
  const sendAnalyticData = () => {
    sendEvent({
      type: 'event',
      container: TagContainer.Analytics,
      payload: {
        event: 'ProductViewDetails',
        button_name: 'view_details',
        product_id: product?.id,
        product_brand: product?.brand?.name,
        product_category: product?.category?.name,
        product_univers: product?.universe?.name,
      } as ProductViewDetails,
    });
  };

  return (
    <div className={styles.heroPdpCTA__viewDetails}>
      <Link
        href={product.path}
        className="vc-btn vc-btn--medium vc-btn--large--md-up vc-btn--full vc-btn--rounded-secondary"
        data-cy="heroPDP_viewDetails"
        onClick={sendAnalyticData}
      >
        <span>{t('PRODUCT.DETAILS.CTA.VIEWDETAILS')}</span>
      </Link>
    </div>
  );
};

// CTA PRODUCT COMPONENT
interface CTAProductBlockProps {
  productShipping: Shipping;
}

const CTAProductBlock: React.FC<CTAProductBlockProps> = ({ productShipping }) => {
  return (
    (productShipping && (
      <NegotiationProvider>
        <CTAProduct
          className={clsx(
            styles.heroPdpCTA__block__recirculation,
            styles['heroPdpCTA__block__recirculation--withMmao'],
          )}
        />
      </NegotiationProvider>
    )) ||
    null
  );
};

// HERO PDP CTA COMPONENT
const HeroPdpCta: React.FC = () => {
  const { product } = useProductQuery();
  const { productShipping } = useProductShippingQuery();
  const { isHeroPDP } = useHeroPdpStyles();
  const { isRecirculation } = usePdpLayout();
  const isSold: boolean = product?.sold;
  const isSellerOnVacation: boolean = product?.seller?.vacation?.active;

  return (
    <div className={styles.heroPdpCTA}>
      {/* RECIRCULATION (PDP PAGE with HeroPDP header) */}
      {!isHeroPDP &&
        isRecirculation &&
        !isSellerOnVacation && ( // on Recirculation we don't show the create alert button
          <CTAProductBlock productShipping={productShipping} />
        )}

      {/* HERO PDP */}
      {isHeroPDP && (
        <>
          {!isSellerOnVacation && !isSold && <CTAProductBlock productShipping={productShipping} />}
          <ViewMoreButton product={product} />
        </>
      )}
    </div>
  );
};

export default HeroPdpCta;
